import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/lib/useBrand";
import {
  Lightning,
  Diamond,
  Target,
  ShieldCheck,
  Clock,
  TrendUp,
  ArrowRight,
  Sparkle,
} from "@phosphor-icons/react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const principles = [
  {
    icon: TrendUp,
    label: "Dream Outcome",
    body: "Reframe what your buyer actually wants. Move from features to specific, vivid results.",
  },
  {
    icon: ShieldCheck,
    label: "Perceived Likelihood",
    body: "Stack proof, mechanism, and guarantees so the buyer feels certain it will work for them.",
  },
  {
    icon: Clock,
    label: "Time Delay",
    body: "Compress the timeline to value. The shorter the gap, the more they pay.",
  },
  {
    icon: Diamond,
    label: "Effort & Sacrifice",
    body: "Remove every step you can. Done-with-you, done-for-you, done-while-you-sleep.",
  },
];

export default function Landing() {
  const { user } = useAuth();
  const { name: appName } = useBrand();
  const navigate = useNavigate();

  const ctaTarget = user ? "/new" : "/login";

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Background image with overlay */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1668456186589-a182ed97df44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxibGFjayUyMGFuZCUyMGdvbGQlMjBhYnN0cmFjdCUyMDNkJTIwc2hhcGVzfGVufDB8fHx8MTc4MTQ1MjgxN3ww&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/40 via-[#0A1628]/85 to-[#0A1628]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-32 md:pt-32 md:pb-40">
          <motion.p
            {...fadeUp}
            className="text-sm tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-6"
            data-testid="hero-eyebrow"
          >
            Inspired by the $100M Offers framework
          </motion.p>
          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.08 }}
            className="font-display font-light text-5xl md:text-7xl lg:text-[88px] tracking-tighter leading-[0.95] max-w-5xl"
            data-testid="hero-title"
          >
            Turn a commoditized offer into a{" "}
            <span className="font-black gold-text">Irresistible Offer</span> they
            feel{" "}
            <em className="not-italic font-black text-white">stupid</em> saying
            no to.
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.16 }}
            className="mt-8 max-w-2xl text-base md:text-lg text-[#9AA5B5] leading-relaxed font-body"
            data-testid="hero-subtitle"
          >
            Feed in your current offer. {appName} applies the proven Value
            Equation, engineers a bonus stack, builds three guarantees, and
            converts you from{" "}
            <span className="text-[#F5F1E8]">&quot;lead gen services&quot;</span>{" "}
            into{" "}
            <span className="text-[#C9A961] font-medium">
              pay-for-performance
            </span>{" "}
            positioning.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.24 }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <Button
              onClick={() => navigate(ctaTarget)}
              className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm px-8 py-6 text-base tracking-wide"
              data-testid="hero-cta-forge"
            >
              <Lightning weight="fill" className="mr-2 h-5 w-5" />
              Forge my Irresistible Offer
            </Button>
            <Link
              to="#how-it-works"
              className="text-sm font-body text-[#F5F1E8] border-b border-white/30 hover:border-[#C9A961] hover:text-[#C9A961] transition-all py-2"
              data-testid="hero-link-how"
            >
              How it works <ArrowRight className="inline h-4 w-4" />
            </Link>
          </motion.div>

          {/* Stat ribbon */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.32 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden max-w-4xl"
          >
            {[
              ["10x", "Perceived value"],
              ["3", "Guarantee variants"],
              ["6+", "Bonuses stacked"],
              ["<2m", "From input to offer"],
            ].map(([k, v]) => (
              <div
                key={v}
                className="bg-[#0A1628] px-6 py-6"
                data-testid={`hero-stat-${v.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="font-display text-3xl md:text-4xl font-black text-[#F5F1E8]">
                  {k}
                </div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#9AA5B5] mt-2">
                  {v}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS — Value Equation */}
      <section id="how-it-works" className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 mb-16">
            <div className="col-span-12 md:col-span-5">
              <p className="text-sm tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-4">
                The Value Equation
              </p>
              <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-tight">
                Four levers.{" "}
                <span className="font-black text-white">One unfair offer.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-base text-[#9AA5B5] leading-relaxed">
                Every Irresistible Offer maximizes the numerator (dream outcome &
                likelihood) and minimizes the denominator (time & effort). We
                instrument all four for you, then stack bonuses to make the
                price feel like a typo.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4">
            {principles.map(({ icon: Icon, label, body }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`bg-[#0F1F38] border border-white/5 hover:border-[#C9A961]/40 transition-all duration-300 rounded-lg p-8 col-span-12 md:col-span-6 ${
                  i % 3 === 0 ? "lg:col-span-3" : "lg:col-span-3"
                }`}
                data-testid={`principle-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon weight="duotone" className="h-8 w-8 text-[#C9A961] mb-6" />
                <div className="font-display font-bold text-lg text-white mb-2">
                  {label}
                </div>
                <p className="text-sm text-[#9AA5B5] leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="relative py-24 md:py-32 border-t border-white/5 bg-gradient-to-b from-transparent to-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-7 bg-[#0F1F38] border border-white/5 rounded-lg p-10 md:p-12">
              <Sparkle weight="fill" className="h-7 w-7 text-[#C9A961] mb-6" />
              <h3 className="font-display text-3xl md:text-4xl font-light tracking-tight mb-4">
                Every offer is delivered as a{" "}
                <span className="font-black">complete sales document</span>.
              </h3>
              <ul className="mt-8 space-y-4 text-[#F5F1E8]">
                {[
                  "Conversion-grade headline + visceral hook",
                  "Full Value Equation breakdown (4 levers)",
                  "4–6 stacked bonuses with perceived value",
                  "Three guarantees: unconditional, conditional, anti",
                  "Ethical scarcity + urgency language",
                  "Pay-for-performance pricing anchor",
                ].map((line) => (
                  <li
                    key={line}
                    className="flex gap-3 text-base font-body text-[#F5F1E8]"
                  >
                    <span className="text-[#C9A961] mt-0.5">▸</span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-span-12 lg:col-span-5 grid grid-cols-1 gap-6">
              <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-8">
                <Target weight="duotone" className="h-7 w-7 text-[#C9A961] mb-4" />
                <div className="font-display font-bold text-lg text-white mb-2">
                  Built for agencies & founders
                </div>
                <p className="text-sm text-[#9AA5B5] leading-relaxed">
                  Marketing agencies, coaches, B2B services, SaaS founders —
                  anyone selling outcomes, not hours.
                </p>
              </div>
              <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-8">
                <ShieldCheck
                  weight="duotone"
                  className="h-7 w-7 text-[#C9A961] mb-4"
                />
                <div className="font-display font-bold text-lg text-white mb-2">
                  Save, share, export
                </div>
                <p className="text-sm text-[#9AA5B5] leading-relaxed">
                  Every offer is saved to your private vault. Export to PDF or
                  share a public link with your team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <p className="text-sm tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-6">
            Stop discounting. Start dominating.
          </p>
          <h2 className="font-display text-4xl md:text-6xl font-light tracking-tighter leading-[0.95]">
            Your <span className="font-black">next offer</span> should sell
            itself.
          </h2>
          <p className="mt-8 text-[#9AA5B5] max-w-2xl mx-auto font-body">
            Sign in with Google, paste your current offer, get back a
            world-class Irresistible Offer in under two minutes.
          </p>
          <Button
            onClick={() => navigate(ctaTarget)}
            className="mt-10 bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm px-10 py-6 text-base tracking-wide"
            data-testid="final-cta-btn"
          >
            <Lightning weight="fill" className="mr-2 h-5 w-5" />
            {user ? "Forge a new offer" : "Sign in & forge my offer"}
          </Button>
        </div>
      </section>

      {/* Footer with required disclaimer */}
      <footer className="border-t border-white/5 py-10 bg-[#070F1C]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-[#808A9B] tracking-wide">
            <div>© {new Date().getFullYear()} {appName} · AAI IT Consultancy &amp; Services Pty Ltd · ABN 95 618 425 915</div>
            <div className="uppercase tracking-[0.25em]">
              Inspired by the &quot;$100M Offers&quot; framework
            </div>
          </div>
          <p
            className="text-[11px] leading-relaxed text-[#808A9B] max-w-3xl"
            data-testid="legal-disclaimer"
          >
            <strong className="text-[#888]">Disclaimer:</strong> This software
            is an independent tool and is not affiliated with, endorsed by, or
            associated with Alex Hormozi or Acquisition.com. The frameworks
            referenced (Value Equation, offer-stacking, guarantees) are
            generally known direct-response copywriting concepts inspired by
            the book &quot;$100M Offers&quot;. All generated content is for
            your own use and review.
          </p>
        </div>
      </footer>
    </div>
  );
}
